import { firstError, unwrap, Widget, WidgetIssueState } from "@nexus/widget-sdk";
import type {
  WidgetDataRequirements,
  WidgetManifest,
  WidgetModule,
  WidgetProps,
} from "@nexus/widget-sdk";
import { EisBadge, REPORT_METRICS_CONTRACT, type ReportMetricsData } from "@nexus/widget-kit";

export interface EisScoreConfig {
  title?: string;
  metricId?: string;
  /** Caption below the value. Defaults to the metric's label when empty. */
  label?: string;
  /** Badge diameter in pixels. */
  size?: number;
  isAvg?: boolean;
}

export const manifest: WidgetManifest = {
  id: "eis-score",
  name: "EIS score",
  description: "One Event Integrity Score as a circular gauge, coloured by the EIS ramp.",
  version: "1.0.0",
  dataRequests: {
    metrics: {
      source: "live-kpi-metrics",
      contract: REPORT_METRICS_CONTRACT,
      configurable: true,
    },
  },
};

export const defaultConfig: EisScoreConfig = { isAvg: true };

export function Component({ data, config }: WidgetProps<EisScoreConfig>) {
  const error = firstError(data, ["metrics"]);
  const result = unwrap<ReportMetricsData>(data.metrics);
  const chosenId = config.metricId?.trim();
  const metric = chosenId
    ? result?.metrics.find((entry) => entry.id === chosenId)
    : result?.metrics[0];
  const loading = data.metrics?.isLoading && !result;
  const heading = config.title?.trim() || manifest.name;
  const caption = config.label?.trim() || metric?.label;

  return (
    <Widget title={heading}>
      {error ? (
        <WidgetIssueState error={error} className="flex-1" />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6">
          {loading ? (
            <p className="text-sm text-secondary" aria-live="polite">
              Loading…
            </p>
          ) : !metric || metric.value === null ? (
            <>
              <EisBadge score={0} isAvg={config.isAvg} size={config.size ?? 96} />
              <p className="text-xs font-medium text-secondary" role="status">
                Not available
              </p>
            </>
          ) : (
            <>
              <EisBadge score={metric.value} isAvg={config.isAvg} size={config.size ?? 96} />
              {caption && <p className="text-sm text-secondary">{caption}</p>}
            </>
          )}
        </div>
      )}
    </Widget>
  );
}

const widgetModule: WidgetModule<EisScoreConfig> = {
  manifest,
  Component,
  defaultConfig,
  getDataRequirements: (config): Readonly<Record<string, WidgetDataRequirements>> => ({
    metrics: {
      mode: "select",
      metricIds: config.metricId?.trim() ? [config.metricId.trim()] : [],
      defaultMetric: !config.metricId?.trim(),
      includeComparison: false,
      fields: ["metrics", "freshness"],
    },
  }),
};
export default widgetModule;
